import { useCallback, useEffect, useState } from 'react';
import { useNavigation } from 'expo-router';

import {
  useCustomizationContext,
  type useInitCustomizationMachine,
} from '@sg/shared-fe-features-customization';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry } from '@order/Telemetry';

import { useCustomizationNavigation } from '../useCustomizationNavigation';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A custom hook to handle the "exit customization" process.
 */
export const useConfirmExitCustomization = (params: Params) => {
  const { actor, restaurantSlug, addressId } = params;

  const { track } = useTelemetry();
  const navigation = useNavigation();

  const { navigateToPreviousScreen } = useCustomizationNavigation({
    restaurantSlug,
    addressId,
  });

  const isConfirmExitEnabled = useFeatureFlag('CELS-4275-order-cancel-confirmation-modal-enabled');

  // ─── Context ───────────────────────────────────────────────────────────────

  const { product, modifications, modifiersCount } = useCustomizationContext({
    actor,
  });

  // ─── State ─────────────────────────────────────────────────────────────────

  const [isConfirmExitModalVisible, setIsConfirmExitModalVisible] = useState(false); // prettier-ignore

  // ─── Flags ─────────────────────────────────────────────────────────────────

  const hasActiveModifications = Boolean(product && modifications && modifiersCount > 0);

  // ─── Helpers ───────────────────────────────────────────────────────────────

  const onExitModalShow = useCallback(() => {
    track('customization.exit-modal.shown');
  }, [track]);

  const cancelExit = useCallback(() => {
    track('customization.exit-modal.stay-on-page-tapped');
    setIsConfirmExitModalVisible(false);
  }, [track]);

  const confirmExit = useCallback(() => {
    track('customization.exit-modal.leave-page-tapped');
    setIsConfirmExitModalVisible(false);
    navigateToPreviousScreen();
  }, [navigateToPreviousScreen, track]);

  /**
   * Toggles gesture navigation based on active modifications.
   */
  const toggleGestureEnabled = useCallback(() => {
    if (!isConfirmExitEnabled) return;

    const parentStack = navigation.getParent();

    if (!parentStack) return;

    parentStack.setOptions({ gestureEnabled: !hasActiveModifications });
  }, [navigation, isConfirmExitEnabled, hasActiveModifications]);

  const showConfirmExitModal = useCallback(() => {
    setIsConfirmExitModalVisible(true);
  }, []);

  // ─── Effects ───────────────────────────────────────────────────────────────

  useEffect(toggleGestureEnabled, [toggleGestureEnabled]);

  // ───────────────────────────────────────────────────────────────────────────

  return {
    isConfirmExitModalVisible,
    onExitModalShow,
    cancelExit,
    confirmExit,
    showConfirmExitModal,
    hasActiveModifications,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  actor: ReturnType<typeof useInitCustomizationMachine>;
  restaurantSlug: string | undefined;
  addressId: string | undefined;
};
